/**
 * SystemDoctrine — merged into the Property-Turnover / Design Studio
 * dashboard per the master continuation schema (Feb 2, 2026).
 *
 * Renders inline inside Design Studio (no new route, no new page).
 * Surfaces:
 *   • Safety & compliance hardwires (OSHA 30 · 48h turn · NSPIRE/City/
 *     Section 8/HUD-VASH ready · handicap accessibility)
 *   • On-Site Acclimation & Jobsite Security Protocol clause
 *   • 50 / 50 pricing model with $0 Design Studio fee
 *   • Four Architectural Unit Variations (Kitchen · Bathroom ·
 *     Media Wall · Ceiling) with "Active Layout Render" status
 *
 * Nomenclature discipline (per schema):
 *   • Uses "Digital Platform" only (never "AI" / "autonomous")
 *   • Uses "variations" (never "palettes" / "color names" / "white box")
 *   • No specific hardware labels — repeatable interchangeable systems
 */

const NAVY = "#030B1A";
const CYAN = "#00FFFF";
const MUTED = "#7E92A8";
const CHROME = "#B4BEC8";
const EMERALD = "#00DC78";
const AMBER = "#FFB347";

const HARDWIRES = [
  { key: "osha",   label: "OSHA 30-Hour · General Construction", note: "All site ops, mobilization, and installation crews." },
  { key: "turn",   label: "48-Hour Turn Capable",                 note: "Fast-turn dispatch spine." },
  { key: "nspire", label: "NSPIRE Code Coalition Ready",          note: "6-zone · 37 code lines · life-safety tiered." },
  { key: "city",   label: "City Code Ready",                      note: "Municipal inspection lens." },
  { key: "s8",     label: "Section 8 / HUD-VASH Ready",           note: "HQS lens + voucher acceptance." },
  { key: "ada",    label: "Handicap Housing Compliance",          note: "Built-in accessibility hardwire." },
];

const VARIATIONS = [
  { key: "kitchen",  title: "Kitchen Variations",    system: "Standardized Repeatable Systems",           icon: "🍳" },
  { key: "bathroom", title: "Bathroom Variations",   system: "Standardized Moisture-Resistant Systems",   icon: "🛁" },
  { key: "media",    title: "Media Wall Variations", system: "Integrated Architectural Focal Units",      icon: "🖼" },
  { key: "ceiling",  title: "Ceiling Variations",    system: "Standardized Interior Overhead Systems",    icon: "☁" },
];

const Row = ({ children, style }) => (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", ...style }}>{children}</div>
);

const Badge = ({ label, note, tone = "cyan" }) => {
  const c = tone === "emerald" ? EMERALD : tone === "amber" ? AMBER : CYAN;
  return (
    <div style={{
      background: `rgba(${tone === "emerald" ? "0,220,120" : tone === "amber" ? "255,179,71" : "0,255,255"}, 0.06)`,
      border: `1px solid ${c}55`, borderRadius: 8, padding: "8px 10px", minWidth: 200, flex: "1 1 220px",
    }}>
      <div style={{ color: c, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.16em", fontWeight: 800 }}>
        ● {label}
      </div>
      {note && <div style={{ color: CHROME, fontSize: "0.72rem", marginTop: 3 }}>{note}</div>}
    </div>
  );
};

const SystemDoctrine = () => (
  <div data-testid="system-doctrine" style={{
    background: "linear-gradient(135deg, rgba(0,255,255,0.05), rgba(167,139,250,0.04))",
    border: `1px solid ${CYAN}33`, borderRadius: 12,
    padding: "14px 16px", marginBottom: 16,
  }}>
    <div style={{ color: CYAN, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.66rem", letterSpacing: "0.22em", fontWeight: 800, marginBottom: 4 }}>
      &gt; SYSTEM DOCTRINE · UNIFIED HARDWIRES · MOOVIN FORWARD DIGITAL PLATFORM
    </div>
    <div style={{ color: "#fff", fontSize: "0.92rem", fontWeight: 700 }}>
      Every unit variation is architected under the same doctrine. No white-box turnovers. No standardized loss of dignity.
    </div>

    {/* Compliance hardwires */}
    <div style={{ marginTop: 12 }}>
      <div style={{ color: MUTED, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.18em", marginBottom: 6 }}>
        SAFETY &amp; COMPLIANCE HARDWIRES
      </div>
      <Row>
        {HARDWIRES.map(h => <Badge key={h.key} label={h.label} note={h.note} tone="emerald" />)}
      </Row>
    </div>

    {/* Site security & acclimation clause */}
    <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(255,179,71,0.05)", border: `1px solid ${AMBER}44`, borderRadius: 10 }}>
      <div style={{ color: AMBER, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.18em", fontWeight: 800, marginBottom: 4 }}>
        ● ON-SITE ACCLIMATION &amp; JOBSITE SECURITY PROTOCOL · ACTIVE HARDWIRE
      </div>
      <div style={{ color: "#fff", fontSize: "0.82rem", lineHeight: 1.5 }}>
        Interchangeable materials <b>must acclimate on-site</b> within controlled climate conditions prior to deployment. During this window, the unit is <b>secured against vagrancy, teenagers kicking in doors, squatters, and unauthorized access</b>.
      </div>
      <div style={{ color: CHROME, fontSize: "0.75rem", marginTop: 6, lineHeight: 1.5 }}>
        <b style={{ color: AMBER }}>Site security line items</b> (covered within the mobilization / site prep cost structure):
        <ul style={{ margin: "4px 0 0 18px", padding: 0, color: CHROME }}>
          <li>Temporary heavy-duty unit lockout + smart-key access control</li>
          <li>Physical security perimeter enforcement during material stabilization</li>
          <li>Secured inventory staging for material theft prevention</li>
          <li>MF-EXT-CLAMP-01 · Property-Owned Exterior Jamb Lock Clamp &amp; Master Padlock Pack ($89, transfers to owner post-turn)</li>
        </ul>
      </div>
    </div>

    {/* 50/50 pricing model */}
    <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
      <div style={{ padding: "12px 14px", background: "rgba(0,255,255,0.06)", border: `1px solid ${CYAN}44`, borderRadius: 10 }}>
        <div style={{ color: CYAN, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.16em" }}>DESIGN STUDIO FEE</div>
        <div style={{ color: "#fff", fontSize: "1.4rem", fontWeight: 800, marginTop: 3 }}>$0 · No Cost</div>
        <div style={{ color: CHROME, fontSize: "0.72rem", marginTop: 2 }}>Studio access is free. Value is in the delivered work.</div>
      </div>
      <div style={{ padding: "12px 14px", background: "rgba(0,220,120,0.06)", border: `1px solid ${EMERALD}44`, borderRadius: 10 }}>
        <div style={{ color: EMERALD, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.16em" }}>MOBILIZATION · FIRST 50%</div>
        <div style={{ color: "#fff", fontSize: "0.98rem", fontWeight: 800, marginTop: 3 }}>User Material Procurement</div>
        <div style={{ color: CHROME, fontSize: "0.72rem", marginTop: 2 }}>Line-item transparent · pass-through cost.</div>
      </div>
      <div style={{ padding: "12px 14px", background: "rgba(0,220,120,0.06)", border: `1px solid ${EMERALD}44`, borderRadius: 10 }}>
        <div style={{ color: EMERALD, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.16em" }}>STABILIZATION · SECOND 50%</div>
        <div style={{ color: "#fff", fontSize: "0.98rem", fontWeight: 800, marginTop: 3 }}>User Stabilization Fee</div>
        <div style={{ color: CHROME, fontSize: "0.72rem", marginTop: 2 }}>(Market Rent × 3) + 20%.</div>
      </div>
    </div>

    {/* Architectural variations */}
    <div style={{ marginTop: 14 }}>
      <div style={{ color: MUTED, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.18em", marginBottom: 6 }}>
        ARCHITECTURAL UNIT VARIATIONS · ACTIVE LAYOUT RENDER
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
        {VARIATIONS.map(v => (
          <div key={v.key} data-testid={`sd-variation-${v.key}`} style={{
            background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10, padding: "10px 12px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "1.15rem" }}>{v.icon}</span>
              <div style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 800 }}>{v.title}</div>
            </div>
            <div style={{ color: CHROME, fontSize: "0.76rem", marginTop: 4 }}>{v.system}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <span style={{ background: "rgba(0,220,120,0.08)", color: EMERALD, border: `1px solid ${EMERALD}55`, borderRadius: 4, fontSize: "0.62rem", padding: "2px 6px", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", fontWeight: 800 }}>INTERCHANGEABLE</span>
              <span style={{ background: "rgba(0,255,255,0.06)", color: CYAN, border: `1px solid ${CYAN}55`, borderRadius: 4, fontSize: "0.62rem", padding: "2px 6px", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", fontWeight: 800 }}>ACTIVE RENDER</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ color: MUTED, fontSize: "0.72rem", marginTop: 8, fontStyle: "italic" }}>
        Every architected system stays interchangeable — the platform never locks a specific hardware label so the same unit variation stays reusable across the portfolio.
      </div>
    </div>
  </div>
);

export default SystemDoctrine;
